import {StyleInfo} from "lit/directives/style-map.js";
import {ENTITY_COLOR_KINDS, EntityColorKind} from "./color-bar.resolver";
import {ColorBar} from "../views/_shared/types/color-bar.interface";

/**
 * Builds per-entity, per-index CSS custom properties
 * (--o2d-color-<entity>-N / --o2d-abbreviation-<entity>-N) so designers can
 * style `.timeline-issue` freely. Also sets --o2d-color-count-<entity> /
 * --o2d-shortcut-count-<entity> (how many entries a kind actually has) for
 * numeric `calc()`/`var()` arithmetic (e.g. `calc(100% / var(--color-count))`
 * to size N equal segments) — `attr()` cannot feed a plain HTML attribute
 * value into `calc()` at all, so that use case needs a custom property.
 * Abbreviation values are wrapped in quotes so they are usable in `content:`.
 *
 * Moved here from timeline-area.factory.ts (2026-08-28, review finding —
 * Linse B): the color-filter radio group (createColorFilterControlAreaConfiguration,
 * timeline-area.factory.ts) is shared between Timeline and Resource Planning,
 * so the .timeline-issue background it controls needs to be built the same
 * way in both, or the radios render but have no visible effect there. A
 * plain `export` out of a file still named/scoped as "timeline-area" made
 * that file a de-facto second shared-helpers module for an unrelated view —
 * living next to color-bar.resolver.ts (whose Record<EntityColorKind,
 * ColorBar[]> output this function consumes) is the correct home instead.
 */
export const buildAllEntitiesStyleMap = (barsByKind: Record<EntityColorKind, ColorBar[]>): StyleInfo => {
    const style: StyleInfo = {};
    for (const kind of ENTITY_COLOR_KINDS) {
        barsByKind[kind].forEach((bar, i) => {
            const n = i + 1;
            if (bar.color !== null) style[`--o2d-color-${kind}-${n}`] = bar.color;
            if (bar.shortcut !== null) style[`--o2d-abbreviation-${kind}-${n}`] = `"${bar.shortcut}"`;
        });
        const colorCount = countNonNull(barsByKind[kind], 'color');
        if (colorCount > 0) style[`--o2d-color-count-${kind}`] = String(colorCount);
        const shortcutCount = countNonNull(barsByKind[kind], 'shortcut');
        if (shortcutCount > 0) style[`--o2d-shortcut-count-${kind}`] = String(shortcutCount);
    }
    return style;
};

/** Counts how many entries have a non-null value for the given field. */
const countNonNull = (bars: ColorBar[], key: 'color' | 'shortcut'): number =>
    bars.reduce((acc, bar) => acc + (bar[key] !== null ? 1 : 0), 0);
